import AdminHome from "@/pages/admin/AdminHome";
import { AudienceProvider } from "@/contexts/AudienceContext";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { CityLayout } from "@/components/CityLayout";
import Directory from "@/pages/Directory";
import Auth from "@/pages/Auth";
import Alerts from "@/pages/Alerts";
import AlertDetail from "@/pages/AlertDetail";
import AlertSubscriptions from "@/pages/AlertSubscriptions";
import SpecialistDashboard from "@/pages/SpecialistDashboard";
import SpecialistProfile from "@/pages/SpecialistProfile";
import { SpecialistAvailability } from "@/pages/SpecialistAvailability";
import SpecialistProfilePage from "@/pages/SpecialistProfilePage";
import ToolPage from "@/pages/tools/ToolPage";
import NotFound from "@/pages/NotFound";
import AdminCorrections from "@/pages/admin/Corrections";
import AdminProfileTraffic from "@/pages/admin/ProfileTraffic";
import AdminInvites from "@/pages/admin/Invites";
import LandingBrandA from "@/pages/compare/LandingBrandA";
import LandingBrandB from "@/pages/compare/LandingBrandB";
import Home from "@/pages/Home";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import SpecialistInvitation from "@/pages/SpecialistInvitation";
import SpecialistOnboarding from "@/pages/SpecialistOnboarding";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import ClaimLink from "@/pages/ClaimLink";
import ConfirmLink from "@/pages/ConfirmLink";
import SpecialtyPage from "@/pages/SpecialtyPage";
import BriefConfirm from "@/pages/BriefConfirm";
import BriefUnsubscribe from "@/pages/BriefUnsubscribe";
import BriefArchive from "@/pages/BriefArchive";
import ReportVerify from "@/pages/ReportVerify";
import AdminBrief from "@/pages/admin/Brief";
import AdminTiles from "@/pages/admin/Tiles";
import BriefPreferences from "@/pages/BriefPreferences";
import Join from "@/pages/Join";
import { BoardShell } from "@/components/board/BoardShell";
import BoardHome from "@/pages/board/BoardHome";
import BoardLearn from "@/pages/board/BoardLearn";
import BoardLearnTile from "@/pages/board/BoardLearnTile";
import BoardResources from "@/pages/board/BoardResources";
import BoardCpd from "@/pages/board/BoardCpd";
import BoardReferrals from "@/pages/board/BoardReferrals";
import BoardSettings from "@/pages/board/BoardSettings";
import AdminMembership from "@/pages/admin/Membership";
import AdminRounds from "@/pages/admin/Rounds";
import { RoundsGate } from "@/components/rounds/RoundsGate";
import Rounds from "@/pages/board/Rounds";
import RoundsCommunity from "@/pages/board/RoundsCommunity";
import RoundsThread from "@/pages/board/RoundsThread";
import RoundsNew from "@/pages/board/RoundsNew";
import RoundsGuidelines from "@/pages/board/RoundsGuidelines";
import Messages from "@/pages/board/Messages";
import MessagesNew from "@/pages/board/MessagesNew";
import MessagesConversation from "@/pages/board/MessagesConversation";
import AccessReportIndex from "@/pages/AccessReportIndex";
import PatientGuide from "@/pages/PatientGuide";
import AreaPage from "@/pages/AreaPage";
import ConditionPage, { ConditionsIndex } from "@/pages/ConditionPage";
import AccessReportIssue from "@/pages/AccessReportIssue";
import AdminAccessReport from "@/pages/admin/AccessReport";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

export default function App() {
  return (
    <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AudienceProvider>
        <BrowserRouter>
          <Routes>
            {/* Brand comparison — standalone, no city context */}
            <Route path="/compare" element={<Navigate to="/compare/brand-a" replace />} />
            <Route path="/compare/brand-a" element={<LandingBrandA />} />
            <Route path="/compare/brand-b" element={<LandingBrandB />} />
            <Route path="/onboarding" element={<SpecialistInvitation />} />

            {/* Admin plane — city context not required */}
            <Route path="/admin" element={<AdminHome />} />
            <Route path="/admin/login" element={<AdminHome />} />
            <Route
              path="/admin/membership"
              element={
                <ProtectedRoute requiredRoles={["super_admin", "pulseboard_admin"]} unauthenticatedRedirect="/admin/login">
                  <AdminMembership />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/corrections"
              element={
                <ProtectedRoute requiredRoles={["super_admin", "pulseboard_admin"]} unauthenticatedRedirect="/admin/login">
                  <AdminCorrections />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/traffic"
              element={
                <ProtectedRoute requiredRoles={["super_admin", "pulseboard_admin"]} unauthenticatedRedirect="/admin/login">
                  <AdminProfileTraffic />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/invites"
              element={
                <ProtectedRoute requiredRoles={["super_admin", "pulseboard_admin"]} unauthenticatedRedirect="/admin/login">
                  <AdminInvites />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/tiles"
              element={
                <ProtectedRoute requiredRoles={["super_admin", "pulseboard_admin"]} unauthenticatedRedirect="/admin/login">
                  <AdminTiles />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/brief"
              element={
                <ProtectedRoute requiredRoles={["super_admin", "pulseboard_admin"]} unauthenticatedRedirect="/admin/login">
                  <AdminBrief />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/rounds"
              element={
                <ProtectedRoute requiredRoles={["super_admin", "pulseboard_admin"]} unauthenticatedRedirect="/admin/login">
                  <AdminRounds />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/access-report"
              element={
                <ProtectedRoute requiredRoles={["super_admin", "pulseboard_admin"]} unauthenticatedRedirect="/admin/login">
                  <AdminAccessReport />
                </ProtectedRoute>
              }
            />
            <Route element={<CityLayout />}>
              <Route path="/admin/*" element={<NotFound />} />
            </Route>

            {/* City-scoped routes — city resolved from subdomain via CityLayout.
                canberra.pulseboard.com.au → slug "canberra".
                localhost falls back to VITE_DEFAULT_CITY_SLUG (default: canberra). */}
            <Route element={<CityLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/claim/:token" element={<ClaimLink />} />
              <Route path="/confirm/:token" element={<ConfirmLink />} />
              <Route path="/directory" element={<Directory />} />
              <Route
                path="/directory/specialist/:slug"
                element={<SpecialistProfilePage />}
              />
              <Route path="/directory/:specialtySlug" element={<SpecialtyPage />} />
              <Route path="/brief" element={<BriefArchive />} />
              <Route path="/access-report" element={<AccessReportIndex />} />
              <Route path="/guide/seeing-a-specialist" element={<PatientGuide />} />
              <Route path="/conditions" element={<ConditionsIndex />} />
              <Route path="/conditions/:conditionSlug" element={<ConditionPage />} />
              <Route path="/directory/area/:areaSlug" element={<AreaPage />} />
              <Route path="/directory/:specialtySlug/:areaSlug" element={<AreaPage />} />
              <Route path="/access-report/:slug" element={<AccessReportIssue />} />
              <Route path="/brief/confirm/:token" element={<BriefConfirm />} />
              <Route path="/brief/unsubscribe/:token" element={<BriefUnsubscribe />} />
              <Route path="/brief/preferences/:token" element={<BriefPreferences />} />
              {/* Clinician material lives behind sign-in (firebase.json 301s these too). */}
              <Route path="/learn" element={<Navigate to="/board/learn" replace />} />
              <Route path="/learn/:id" element={<LearnRedirect />} />
              <Route path="/report/verify/:token" element={<ReportVerify />} />
              <Route path="/resources" element={<Navigate to="/board/resources" replace />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/join" element={<Join />} />
              <Route path="/alerts" element={<Alerts />} />
              <Route
                path="/alerts/subscriptions"
                element={
                  <ProtectedRoute requiredRoles={["gp", "specialist"]}>
                    <AlertSubscriptions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/alerts/:id"
                element={
                  <ProtectedRoute requiredRoles={["gp", "specialist"]}>
                    <AlertDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/gp-dashboard"
                element={
                  <ProtectedRoute requiredRoles={["gp"]}>
                    <Navigate to="/board" replace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/board"
                element={
                  <ProtectedRoute
                    requiredRoles={["gp", "specialist", "super_admin", "pulseboard_admin"]}
                    unauthenticatedRedirect="/auth"
                  >
                    <BoardShell />
                  </ProtectedRoute>
                }
              >
                <Route index element={<BoardHome />} />
                <Route path="learn" element={<BoardLearn />} />
                <Route path="learn/:id" element={<BoardLearnTile />} />
                <Route path="cpd" element={<BoardCpd />} />
                <Route path="resources" element={<BoardResources />} />
                <Route path="referrals" element={<BoardReferrals />} />
                <Route path="settings" element={<BoardSettings />} />
                <Route path="rounds" element={<RoundsGate />}>
                  <Route index element={<Rounds />} />
                  <Route path="new" element={<RoundsNew />} />
                  <Route path="c/:slug" element={<RoundsCommunity />} />
                  <Route path="t/:id" element={<RoundsThread />} />
                  <Route path="guidelines" element={<RoundsGuidelines />} />
                </Route>
                <Route path="messages" element={<Messages />} />
                <Route path="messages/new" element={<MessagesNew />} />
                <Route path="messages/:id" element={<MessagesConversation />} />
              </Route>
              <Route
                path="/specialist-dashboard"
                element={
                  <ProtectedRoute requiredRoles={["specialist"]}>
                    <SpecialistDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/specialist-profile"
                element={
                  <ProtectedRoute requiredRoles={["specialist"]}>
                    <SpecialistProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/specialist-profile/:id"
                element={
                  <ProtectedRoute requiredRoles={["specialist"]}>
                    <SpecialistProfile />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Specialist self-reported availability portal */}
            <Route element={<CityLayout />}>
              <Route
                path="/specialist/onboarding"
                element={
                  <ProtectedRoute requiredRoles={["specialist"]}>
                    <SpecialistOnboarding />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/specialist/availability"
                element={
                  <ProtectedRoute requiredRoles={["specialist"]}>
                    <SpecialistAvailability />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Clinical tools are members-only, like the rest of the clinician material. */}
            <Route element={<CityLayout />}>
              <Route
                path="/tools/:slug"
                element={
                  <ProtectedRoute
                    requiredRoles={["gp", "specialist", "super_admin", "pulseboard_admin"]}
                    unauthenticatedRedirect="/auth"
                  >
                    <ToolPage />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* NotFound renders the public frame, which needs the city. */}
            <Route element={<CityLayout />}>
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </BrowserRouter>
        </AudienceProvider>
      </AuthProvider>
    </QueryClientProvider>
    </ErrorBoundary>
  );
}

function LearnRedirect() {
  const { id } = useParams();
  return <Navigate to={`/board/learn/${id ?? ""}`} replace />;
}
